// Utility for converting HEIC/HEIF images (common from iPhones) to JPEG so
// they can be uploaded and displayed by every browser. iOS Safari sometimes
// reports a HEIC file as image/heic, image/heif, or even an empty mime type,
// so we detect by extension as well.

let heic2anyPromise = null;

function loadHeic2Any() {
  if (!heic2anyPromise) {
    heic2anyPromise = import('heic2any').then((m) => m.default || m);
  }
  return heic2anyPromise;
}

export function isHeicFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence' || type === 'image/heif-sequence') {
    return true;
  }
  const name = (file.name || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * Convert a HEIC/HEIF File to a JPEG File. Non-HEIC files are returned unchanged.
 * @param {File} file
 * @param {{ quality?: number }} [opts]
 * @returns {Promise<File>}
 */
export async function convertHeicToJpeg(file, opts = {}) {
  if (!file || !isHeicFile(file)) return file;

  const quality = typeof opts.quality === 'number' ? opts.quality : 0.9;

  try {
    const heic2any = await loadHeic2Any();
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality,
    });

    const blob = Array.isArray(converted) ? converted[0] : converted;
    const baseName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '');
    const newName = `${baseName || 'photo'}.jpg`;

    return new File([blob], newName, {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch (err) {
    console.error('HEIC conversion failed:', err);
    throw new Error('Could not convert HEIC image. Please try a JPG or PNG.');
  }
}

/**
 * Convert an array/FileList of files, converting any HEIC entries to JPEG.
 * @param {FileList|File[]} files
 * @param {{ quality?: number }} [opts]
 * @returns {Promise<File[]>}
 */
export async function convertHeicFiles(files, opts = {}) {
  const arr = Array.from(files || []);
  return Promise.all(arr.map((f) => convertHeicToJpeg(f, opts)));
}

// Accept attribute value that works for <input type="file"> pickers.
// Including the extensions helps iOS browsers that don't surface HEIC under image/*.
export const IMAGE_ACCEPT_WITH_HEIC = 'image/*,image/heic,image/heif,.heic,.heif';
